import { makeStyles, SearchBox } from '@fluentui/react-components';
import { useBoolean } from '@fluentui/react-hooks';
import React, { KeyboardEvent, ReactEventHandler, useState } from 'react';

export interface IFilterSearchBarProps {
    searchBoxPlaceholderText: string;
    onSearchBoxChange: (ev: React.ChangeEvent<HTMLInputElement> | undefined, newValue: string | undefined) => void;
    onAbort?: ReactEventHandler<HTMLInputElement>;
    onClear?: ReactEventHandler<HTMLInputElement>;
    shouldFocusOnFirstItemOnTab?: boolean;
}

const useStyles = makeStyles({
    searchBox: {
        width: '510px',
        minWidth: '510px',
        maxWidth: '510px',
    },
});

const FilterSearchBar: React.FC<IFilterSearchBarProps> = ({
    searchBoxPlaceholderText,
    onSearchBoxChange,
    onAbort,
    onClear,
    shouldFocusOnFirstItemOnTab = true,
}) => {
    const styles = useStyles();
    const [value, setValue] = useState('');
    const [isSearchboxFocused, { setTrue: setIsSearchboxFocusedTrue, setFalse: setIsSearchboxFocusedFalse }] = useBoolean(false);

    const focusOnFirstItemOnKeyDown = (ev: KeyboardEvent<HTMLInputElement>) => {
        if ((ev.key === 'Tab' || ev.key === 'ArrowDown') && shouldFocusOnFirstItemOnTab) {
            ev.preventDefault();
            ev.stopPropagation();
            document.getElementById('item-0')?.focus();
        }
    };

    const handleChange = (ev: React.ChangeEvent<HTMLInputElement> | React.MouseEvent<HTMLSpanElement>, data: { value: string }) => {
        const nextValue = data.value;
        const wasCleared = nextValue === '' && value !== '';
        setValue(nextValue);
        onSearchBoxChange(ev as React.ChangeEvent<HTMLInputElement> | undefined, nextValue);

        if (wasCleared && ev.type === 'click') {
            onClear?.(ev as unknown as React.SyntheticEvent<HTMLInputElement>);
        }
    };

    const handleKeyDown = (ev: KeyboardEvent<HTMLInputElement>) => {
        focusOnFirstItemOnKeyDown(ev);

        if (ev.key === 'Escape') {
            setValue('');
            onSearchBoxChange(undefined, '');
            onAbort?.(ev);
        }
    };

    return (
        <SearchBox
            value={value}
            className={styles.searchBox}
            onFocus={setIsSearchboxFocusedTrue}
            onBlur={setIsSearchboxFocusedFalse}
            onKeyDown={handleKeyDown}
            onChange={handleChange}
            placeholder={isSearchboxFocused ? '' : searchBoxPlaceholderText}
            aria-label={searchBoxPlaceholderText}
        />
    );
};

export default FilterSearchBar;
